/**
 * SettingsPageV2 — Suite-standard settings hub (UI composition only).
 *
 * This file is a PURE COMPOSER. It does not transform data. All mapping
 * goes through `adapters/settingsAdapter.ts`. All data fetching lives
 * in dedicated hooks (`useMembersTab`, `useBillingTabFacts`,
 * `useNotificationPreferences`).
 *
 * Sections rendered (via `@levelup/ui-management/settings`):
 *   - ProfileSection           — Central-owned editing (open Central with returnUrl)
 *   - WorkspaceSection         — Central-owned editing (open Central with returnUrl)
 *   - Members                  — Central-direct CRUD via membershipApi (Bearer)
 *   - BillingSection           — BeHeard RTK Query → Stripe portal
 *   - NotificationPreferences  — Real Central fetch (loading / error / not-configured / ready)
 *   - Role & Access (extra)    — BeHeard-specific app role display + permission rows
 */

import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import {
  SettingsShell,
  ProfileSection,
  WorkspaceSection,
  BillingSection,
  NotificationPreferencesSection,
  SETTINGS_TAB_IDS,
  type SettingsTabConfig,
} from '@levelup/ui-management/settings';
import {
  MembershipList,
  PendingInvitesList,
  InviteMemberModal,
  RoleBadge,
} from '@levelup/ui-management/membership';
import type { CentralRole } from '@levelup/central-auth';

import { selectBootCurrentWorkspace } from '../store/slices/bootContextSlice';
import { useCentralAuth } from '../hooks/useCentralAuth';
import { useCentralConsumer } from '../hooks/useCentralConsumer';
import { useAppRole } from '../hooks/useAppRole';
import { useMembersTab } from '../hooks/useMembersTab';
import { useBillingTabFacts } from '../hooks/useBillingTabFacts';
import { useNotificationPreferences } from '../hooks/useNotificationPreferences';
import { AppRoleBadgeList } from '../components/AppRoleBadge';
import { SuggestedRoleBundles } from '../components/SuggestedRoleBundles';
import { CENTRAL_URL } from '../config/centralAuth';
import { getSimulatedCentralRole } from '../auth/simulatedCentralRole';
import { getSimulatedAppProfile } from '../auth/simulatedAppProfile';

import {
  buildBillingSectionProps,
  buildMembershipPermissions,
  buildNotificationsSectionProps,
  buildProfileSectionProps,
  buildWorkspaceSectionProps,
  isNotificationsNotConfigured,
  type BootWorkspaceLike,
  type CentralUserLike,
} from '../adapters/settingsAdapter';

// ──────────────────────────────────────────────────────────────
// Tab catalog
// ──────────────────────────────────────────────────────────────

const ROLE_TAB: SettingsTabConfig = {
  id: 'role',
  label: 'Role & Access',
  order: 600,
};

const EXTRA_TABS: SettingsTabConfig[] = [ROLE_TAB];

const SHELL_TAB_IDS = new Set<string>([
  SETTINGS_TAB_IDS.PROFILE,
  SETTINGS_TAB_IDS.WORKSPACE,
  SETTINGS_TAB_IDS.MEMBERS,
  SETTINGS_TAB_IDS.BILLING_CREDITS,
  SETTINGS_TAB_IDS.NOTIFICATIONS,
  ROLE_TAB.id,
]);

function tabFromPath(pathname: string): string {
  const segment = pathname.split('/').filter(Boolean).pop();
  if (!segment || segment === 'settings') return SETTINGS_TAB_IDS.PROFILE;
  if (segment === 'billing') return SETTINGS_TAB_IDS.BILLING_CREDITS;
  return segment;
}

// ──────────────────────────────────────────────────────────────
// Page (UI composition only)
// ──────────────────────────────────────────────────────────────

export function SettingsPageV2() {
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(() => tabFromPath(location.pathname));
  useEffect(() => setActiveTab(tabFromPath(location.pathname)), [location.pathname]);

  const handleTabChange = useCallback(
    (tabId: string) => {
      setActiveTab(tabId);
      const slug = tabId === SETTINGS_TAB_IDS.BILLING_CREDITS ? 'billing' : tabId;
      navigate(`/settings/${slug}`, { replace: true });
    },
    [navigate],
  );

  if (!SHELL_TAB_IDS.has(activeTab)) return null;

  return (
    <SettingsShell
      activeTab={activeTab}
      onTabChange={handleTabChange}
      extraTabs={EXTRA_TABS}
      title="Settings"
    >
      {activeTab === SETTINGS_TAB_IDS.PROFILE && <ProfileTab />}
      {activeTab === SETTINGS_TAB_IDS.WORKSPACE && <WorkspaceTab />}
      {activeTab === SETTINGS_TAB_IDS.MEMBERS && <MembersTab />}
      {activeTab === SETTINGS_TAB_IDS.BILLING_CREDITS && <BillingTab />}
      {activeTab === SETTINGS_TAB_IDS.NOTIFICATIONS && <NotificationsTab />}
      {activeTab === ROLE_TAB.id && <RoleAccessTab />}
    </SettingsShell>
  );
}

// ──────────────────────────────────────────────────────────────
// Profile tab — composition only
// ──────────────────────────────────────────────────────────────

function ProfileTab() {
  const { user } = useCentralAuth();
  const props = buildProfileSectionProps({
    user: user as CentralUserLike | null,
    centralUrl: CENTRAL_URL,
    currentHref: window.location.href,
  });
  return (
    <>
      <ProfileSection {...props} />
      <CentralEditHint text="Profile changes are saved across all LevelUp apps." />
    </>
  );
}

// ──────────────────────────────────────────────────────────────
// Workspace tab — composition only
// ──────────────────────────────────────────────────────────────

function WorkspaceTab() {
  const workspace = useSelector(selectBootCurrentWorkspace) as BootWorkspaceLike | null;
  const { hasWorkspacePermission } = useCentralConsumer();
  const props = buildWorkspaceSectionProps({
    workspace,
    canTransferOwnership: hasWorkspacePermission('transferOwnership'),
    centralUrl: CENTRAL_URL,
    currentHref: window.location.href,
  });
  return (
    <>
      <WorkspaceSection {...props} />
      <CentralEditHint text="Workspace settings are shared across all apps in this workspace." />
    </>
  );
}

// ──────────────────────────────────────────────────────────────
// Members tab — composition only (data via useMembersTab,
// permissions object via adapter)
// ──────────────────────────────────────────────────────────────

function MembersTab() {
  const workspace = useSelector(selectBootCurrentWorkspace) as BootWorkspaceLike | null;
  const { user } = useCentralAuth();
  const { hasWorkspacePermission } = useCentralConsumer();

  const canManageMembers = hasWorkspacePermission('manageMembers');
  const currentRole = (workspace?.role as CentralRole) ?? 'viewer';
  const workspaceId = workspace?.id ?? '';

  const permissions = buildMembershipPermissions({
    currentRole,
    canManageMembers,
  });

  const ctrl = useMembersTab({ workspaceId, canManageMembers });

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Workspace</h2>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Name</span>
            <span className="text-sm font-medium text-gray-900">
              {workspace?.name || '—'}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Your Role</span>
            {workspace?.role ? (
              <RoleBadge role={workspace.role as CentralRole} />
            ) : (
              <span className="text-sm font-medium text-gray-900">—</span>
            )}
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900">Members</h3>
          {permissions.canInvite && permissions.selectableRoles.length > 0 && (
            <button
              onClick={() => ctrl.setInviteModalOpen(true)}
              className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Invite Member
            </button>
          )}
        </div>
        <MembershipList
          members={ctrl.members}
          permissions={permissions}
          currentUserId={user?.id ?? ''}
          isLoading={ctrl.isLoadingMembers}
          error={ctrl.membersError}
          onUpdateRole={canManageMembers ? ctrl.handleUpdateRole : undefined}
          onRemoveMember={canManageMembers ? ctrl.handleRemoveMember : undefined}
        />
      </div>

      {canManageMembers && (
        <div>
          <h3 className="text-base font-semibold text-gray-900 mb-3">Pending Invites</h3>
          <PendingInvitesList
            invites={ctrl.invites}
            permissions={permissions}
            isLoading={ctrl.isLoadingInvites}
            error={ctrl.invitesError}
            onResendInvite={ctrl.handleResendInvite}
            onCancelInvite={ctrl.handleCancelInvite}
          />
        </div>
      )}

      <InviteMemberModal
        open={ctrl.inviteModalOpen}
        onOpenChange={ctrl.setInviteModalOpen}
        permissions={permissions}
        onInvite={ctrl.handleInvite}
        isSubmitting={ctrl.inviteSubmitting}
        error={ctrl.inviteError}
        inviteRoleOptions={ctrl.inviteRoleOptions.length > 0 ? ctrl.inviteRoleOptions : undefined}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Billing tab — composition only
// ──────────────────────────────────────────────────────────────

function BillingTab() {
  const { hasWorkspacePermission } = useCentralConsumer();
  const { facts, openPortal } = useBillingTabFacts({
    canManage: hasWorkspacePermission('manageBilling'),
  });
  const props = buildBillingSectionProps({ facts, onPortal: openPortal });
  return <BillingSection {...props} />;
}

// ──────────────────────────────────────────────────────────────
// Notifications tab — real Central fetch via useNotificationPreferences;
// adapter renders proper loading / error / not-configured / ready states.
// ──────────────────────────────────────────────────────────────

function NotificationsTab() {
  const { state, updateCategory } = useNotificationPreferences();
  const props = buildNotificationsSectionProps({
    state,
    onUpdateCategory: updateCategory,
  });
  return (
    <>
      {isNotificationsNotConfigured(state) && <NoCategoriesNotice />}
      <NotificationPreferencesSection {...props} />
    </>
  );
}

function NoCategoriesNotice() {
  return (
    <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex gap-3">
      <svg
        className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
      </svg>
      <div>
        <p className="text-sm font-medium text-blue-900">No notification categories yet</p>
        <p className="text-xs text-blue-800/80 mt-0.5">
          Notification categories will appear here as soon as Central publishes them
          for your workspace. You can return to this tab to configure delivery channels
          (inbox, email, push) per category once they are available.
        </p>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Role & Access tab — BeHeard-specific; preserves existing displays.
// ──────────────────────────────────────────────────────────────

function RoleAccessTab() {
  const workspace = useSelector(selectBootCurrentWorkspace);
  const { hasWorkspacePermission } = useCentralConsumer();
  const appRole = useAppRole();
  const canManageMembers = hasWorkspacePermission('manageMembers');

  // Surface Testing-Mode simulation status (UI-only — backend remains
  // authoritative on every API call). Read at render time so a Clear
  // performed in another tab is reflected on the next mount/reload.
  const simulatedRole = getSimulatedCentralRole();
  const simulatedProfile = getSimulatedAppProfile();
  const isSimulating = Boolean(simulatedRole || simulatedProfile);

  return (
    <div className="space-y-6">
      {isSimulating && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-amber-900">Testing Mode active</p>
          <p className="text-xs text-amber-800/80 mt-1 leading-snug">
            You're previewing this app as
            {simulatedRole ? <> Central role <strong>{simulatedRole}</strong></> : ' your real Central role'}
            {simulatedProfile && simulatedProfile.length > 0 && (
              <> with BeHeard profile bundle <strong>{simulatedProfile.join(', ')}</strong></>
            )}
            . Backend authorization is unchanged — the server still enforces your real capabilities.
            Clear it from the testing-mode panel in the navbar.
          </p>
        </div>
      )}

      {appRole && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="font-semibold text-gray-900 mb-3">Your BeHeard Access</h3>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium mb-2">Central Role</p>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 capitalize">
                {(workspace?.role as string) ?? 'unknown'}
              </span>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium mb-2">App Role Profile(s)</p>
              <AppRoleBadgeList profileKeys={appRole.profiles} size="md" />
            </div>
            <p className="text-sm text-gray-600">{appRole.summary}</p>
          </div>
        </div>
      )}

      {workspace?.permissions && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Your Permissions</h3>
          <div className="space-y-3">
            <PermissionRow label="Manage Members" granted={workspace.permissions.manageMembers} />
            <PermissionRow label="Manage Billing" granted={workspace.permissions.manageBilling} />
            <PermissionRow label="Transfer Ownership" granted={workspace.permissions.transferOwnership} />
          </div>
        </div>
      )}

      {canManageMembers && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="font-semibold text-gray-900">Role Assignment Guide</h3>
          <p className="text-xs text-gray-500 mt-0.5 mb-4">
            Assign these scope bundles to workspace members in Central.
          </p>
          <SuggestedRoleBundles />
        </div>
      )}
    </div>
  );
}

function PermissionRow({ label, granted }: { label: string; granted: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-700">{label}</span>
      {granted ? (
        <span className="flex items-center gap-1 text-xs text-green-700 font-medium">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Granted
        </span>
      ) : (
        <span className="text-xs text-gray-400">Not granted</span>
      )}
    </div>
  );
}

function CentralEditHint({ text }: { text: string }) {
  return (
    <p className="mt-4 text-xs text-gray-400 flex items-center gap-1.5">
      <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
      </svg>
      {text}
    </p>
  );
}
